import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Plus, X } from 'lucide-react';
import SpotifySongSearch from '@/components/common/SpotifySongSearch';
import { SpotifyTrack } from '@/services/spotifyService';

interface DisplaySong {
  id?: string;
  title: string;
  artist: string;
  duration?: string;
  year_introduced: number;
  youtube_url?: string;
  display_year_id?: string;
}

interface SongsSectionProps {
  songs: DisplaySong[];
  maxSongs?: number;
  onSongsChange: (songs: DisplaySong[]) => void;
}

const SongsSection: React.FC<SongsSectionProps> = ({
  songs,
  maxSongs = 10,
  onSongsChange,
}) => {
  const currentYear = new Date().getFullYear();

  const addSong = () => {
    if (songs.length >= maxSongs) return;
    
    const newSong: DisplaySong = {
      title: '',
      artist: '',
      year_introduced: currentYear,
      youtube_url: ''
    };
    onSongsChange([...songs, newSong]);
  };

  const removeSong = (index: number) => {
    if (songs.length <= 1) return; // Keep at least one song
    const newSongs = songs.filter((_, i) => i !== index);
    onSongsChange(newSongs);
  };

  const updateSong = (index: number, updates: Partial<DisplaySong>) => {
    const newSongs = songs.map((song, i) => 
      i === index ? { ...song, ...updates } : song
    );
    onSongsChange(newSongs);
  };

  const handleSpotifySelect = (index: number, track: SpotifyTrack) => {
    updateSong(index, {
      title: track.name,
      artist: track.artists[0]?.name || '',
      duration: track.duration_ms ? `${Math.floor(track.duration_ms / 60000)}:${String(Math.floor((track.duration_ms % 60000) / 1000)).padStart(2, '0')}` : undefined
    });
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex justify-between items-center">
          <span>Songs ({songs.length}/{maxSongs})</span>
          <Button
            type="button"
            onClick={addSong}
            disabled={songs.length >= maxSongs}
            size="sm"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Song
          </Button>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {songs.map((song, index) => (
          <div key={index} className="relative border rounded-lg p-4">
            {songs.length > 1 && (
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="absolute top-2 right-2"
                onClick={() => removeSong(index)}
              >
                <X className="h-4 w-4" />
              </Button>
            )}

            <div className="space-y-4 pr-10">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor={`song-title-${index}`}>Song Title</Label>
                  <Input
                    id={`song-title-${index}`}
                    value={song.title}
                    onChange={(e) => updateSong(index, { title: e.target.value })}
                    placeholder="Enter song title"
                  />
                </div>
                <div>
                  <Label htmlFor={`song-artist-${index}`}>Artist</Label>
                  <Input
                    id={`song-artist-${index}`}
                    value={song.artist}
                    onChange={(e) => updateSong(index, { artist: e.target.value })}
                    placeholder="Enter artist name"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor={`song-duration-${index}`}>Duration (MM:SS)</Label>
                  <Input
                    id={`song-duration-${index}`}
                    value={song.duration || ''}
                    onChange={(e) => updateSong(index, { duration: e.target.value })}
                    placeholder="3:45"
                  />
                </div>
                <div>
                  <Label htmlFor={`song-year-${index}`}>Year Introduced</Label>
                  <Input
                    id={`song-year-${index}`}
                    type="number"
                    min="2000"
                    max={currentYear + 1}
                    value={song.year_introduced}
                    onChange={(e) => updateSong(index, { year_introduced: parseInt(e.target.value) || currentYear })}
                  />
                </div>
              </div>

              <div>
                <Label htmlFor={`song-youtube-${index}`}>YouTube URL (Optional)</Label>
                <Input
                  id={`song-youtube-${index}`}
                  value={song.youtube_url || ''}
                  onChange={(e) => updateSong(index, { youtube_url: e.target.value })}
                  placeholder="https://youtube.com/watch?v=..."
                />
              </div>

              <div>
                <Label>Find on Spotify</Label>
                <div className="mt-1">
                  <SpotifySongSearch
                    onSelect={(track) => handleSpotifySelect(index, track)}
                    placeholder="Search for song on Spotify..."
                  />
                </div>
              </div>
            </div>
          </div>
        ))}

        {songs.length === 0 && (
          <div className="text-center py-8 text-muted-foreground">
            <p>No songs added yet</p>
            <Button type="button" onClick={addSong} className="mt-2">
              <Plus className="h-4 w-4 mr-2" />
              Add Your First Song
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default SongsSection;